import { useHistory, type match } from 'react-router-dom'
import { Alert, PageHeader, message } from 'antd'
import { Box } from '@weareberyl/design-system'
import type { DeepPartial } from '@apollo/client/utilities'

import AddonForm from '../AddonForm'
import {
  AddonType,
  SchemeAddonListDocument,
  useCreateSchemeAddonMutation,
  type SchemeAddon,
  type SchemeAddonInput,
} from '~/gql/generated/graphql'
import { prependScheme } from '~/constants/routes'

type Props = {
  match: match<{ scheme_id: string; id: string }>
}

const AddonCreate = ({ match }: Props) => {
  const history = useHistory()
  const [createSchemeAddon, { loading: saving, error }] =
    useCreateSchemeAddonMutation()

  // Display messages from the server if there are errors that get past client side form validation
  const messages = error?.graphQLErrors.map(error => error.message)

  const onFinish = async (schemeAddonInput: SchemeAddonInput) => {
    try {
      const { data } = await createSchemeAddon({
        variables: { schemeAddonInput },
        refetchQueries: [SchemeAddonListDocument],
      })

      history.replace(
        prependScheme(
          `/addons/${data?.add_scheme_addon.id}`,
          match.params.scheme_id,
        ),
      )
    } catch {
      message.error('There was an error saving the addon')
      return
    }

    message.success('Addon created')
  }

  const initialValues: DeepPartial<SchemeAddon> = {
    scheme_id: match.params.scheme_id,
    // There's only this type currently
    addon_type: AddonType.free_unlocks,
    is_active: false,
    is_protected: false,
    role_whitelist: [],
  }

  return (
    <>
      <PageHeader title="Create new addon" onBack={history.goBack} />
      {messages && (
        <Box pb={3}>
          <Alert
            message="Couldn't create the addon"
            description={messages.map(message => (
              <p key={message}>{message}</p>
            ))}
            type="error"
            showIcon
          />
        </Box>
      )}
      <AddonForm
        initialValues={initialValues}
        loading={saving}
        onFinish={onFinish}
      />
    </>
  )
}

export default AddonCreate
